import { useTranslation } from "react-i18next";
import React, { Suspense, useMemo, useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import { FeatureKeysType, roleAccessType } from "src/pages/Studies/models/interfaces";
import { Button, Result } from "src/assets/imports/antdComponents";
import Loading from "../components/LoadingComp";
import { useFeatures } from "./featureManager";
import { environment } from "./environment";
import { adminMenuItems, newMenuItems, developerMenuItems, observerMenuItems } from "./menuConfig";

const RoutesConfig: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isFeatureAvailable } = useFeatures();
	const { role } = useSelector((state: RootState) => state.studiesSlice);
	const isNotesEnabled = isFeatureAvailable(FeatureKeysType.Notes);
	const isAskKuralEnabled = isFeatureAvailable(FeatureKeysType.AskKural);
	const isStudyUpsert = isFeatureAvailable(FeatureKeysType.StudyUpsert);
	const isUserGuideEnabled = isFeatureAvailable(FeatureKeysType.UserGuide);
	const isPersonasEnabled = isFeatureAvailable(FeatureKeysType.Personas);
	const isDigitalTwinEnabled = isFeatureAvailable(FeatureKeysType.DigitalTwin);
	const isAdminTranscriptExportEnabled = isFeatureAvailable(FeatureKeysType.AdminTranscriptExport);
	const isAdmin = role === roleAccessType.Admin;
	const isSupport = role === roleAccessType.Support;
	const isDeveloper = role === roleAccessType.Developer;
	const isAdminOrSupport = isAdmin || isSupport;
	const isObserver = role === roleAccessType.Observer;
	const isRestrictedRole = isAdmin || isSupport || isDeveloper || isObserver;
	const isMonitoringAvailable = isFeatureAvailable(FeatureKeysType.UserActivityMonitoring);

	const filteredMenu = useMemo(() => {
		let menu;

		if (isAdmin) {
			menu = adminMenuItems;
		} else if (isSupport) {
			menu = adminMenuItems.filter((item) => item.supportEnabled);
		} else if (isDeveloper) {
			menu = developerMenuItems;
		} else if (isObserver) {
			menu = observerMenuItems; // Remove the isMonitoringAvailable filter — Observer only has monitoring, always show it
		} else {
			menu = newMenuItems;
		}

		const featureMap: Record<string, boolean> = {
			// askKural: isAskKuralEnabled,
			taxonomy: isNotesEnabled,
			addStudy: isStudyUpsert,
			manageStudies: isStudyUpsert,
			redirectToPage: !!environment.REACT_APP_REDIRECT_PAGE_URL,
			monitoring: isMonitoringAvailable,
			transcriptExport: isAdminTranscriptExportEnabled,
			"user-guide": isUserGuideEnabled,
			"insilico-landing": isPersonasEnabled,
			personas: isPersonasEnabled,
			"persona-project": isPersonasEnabled,
			"persona-setup": isPersonasEnabled,
			"persona-chat": isPersonasEnabled,
			"dt-projects": isDigitalTwinEnabled,
		};

		// For Observer, don't filter by featureMap — monitoring route must always be accessible
		if (isObserver) return menu;

		const filtered = menu.filter((item) => featureMap[item.key] !== false);
		return filtered;
	}, [
		isAdmin,
		isSupport,
		isDeveloper,
		isObserver,
		isNotesEnabled,
		isAskKuralEnabled,
		isStudyUpsert,
		isUserGuideEnabled,
		isMonitoringAvailable,
		isPersonasEnabled,
		isDigitalTwinEnabled,
		isAdminTranscriptExportEnabled,
	]);

	const rootRedirectPath = useMemo(() => {
		if (isObserver) return "/monitoring";
		if (isAdminOrSupport) return "/admin-center";
		if (isDeveloper) return "/manage-api-keys";
		return "/studies";
	}, [isObserver, isAdminOrSupport, isDeveloper]);

	// Note: hard 403s (account disabled, access expired, etc.) are handled at the AppContent
	// level by `<ForbiddenScreen />`, which fully replaces the layout. This component is never
	// rendered in that state, so no `isNavDisabled` guard is needed here.
	return (
		<Suspense fallback={<Loading />}>
			<Routes>
				<Route path="/" element={<Navigate to={rootRedirectPath} replace />} />
				{filteredMenu.map((item) => (
					<Route key={item.key} path={item.path} element={item.component ? <item.component /> : <div>{`Component for ${item.path} is not defined`}</div>} />
				))}

				<Route
					path="*"
					element={
						<div className="scale-baseline-100 flex items-center justify-center h-full w-full bg-white">
							<Result
								status="404"
								title=""
								subTitle={t("Sorry, the page you visited does not exist.")}
								extra={
									<Button onClick={() => navigate("/")} type="primary">
										{t("Back Home")}
									</Button>
								}
							/>
						</div>
					}
				/>
			</Routes>
		</Suspense>
	);
};

export default RoutesConfig;
